import * as THREE from "three";
import { Loader } from "../assets/loader";
import { Utils } from "../../utils/utils";
import { Slot } from "@/types/slotsData";
import { Artwork, ArtworkWrapper } from "@/types/artworksData";
import AppData from "../../app.data";
import SoundService from "../../services/sound/sound.service";
import { Artwork3dModel } from "./managers/model3D.manager";
import { ArtworkMedia } from "./managers/media.manager";
import { ArtworkHandler } from "./managers/artwork.handler";
import { ArtworkFrame } from "./attachments/frame.attachment";
import { ArtworkPedestal } from "./attachments/pedestal.attachment";
import {
  resolveArtworkForRuntimeMedia,
  resolveArtworkModelSource,
} from "./artwork.runtime.assets";
import { cleanupArtworkRuntimeRegistries } from "../../utils/runtime-registry.utils";
import {
  rankArtworkWrappersForProgressiveLoad,
  runProgressiveArtworkQueue,
  type ProgressiveArtworkQueueSummary,
} from "./progressiveArtworkQueue";
import {
  createDisposalAccounting,
  type DisposalAccounting,
  type DisposalAccountingHandle,
  type DisposalAccountingSnapshot,
} from "./disposalAccounting";
import type { ArtetraRuntimeAssetHandle } from "../../runtime/artetraRuntimeSession";
import type { RuntimeMediaDelivery } from "@/utils/runtimeConfig";

const appData = new AppData()
const EDITABLE_SPACE_TYPES = ['open', 'hexa', 'procedural', 'custom'];
const PROGRESSIVE_INITIAL_BUDGET = 4;
const PROGRESSIVE_BACKGROUND_CONCURRENCY = 1;
const PROGRESSIVE_BACKGROUND_DELAY_MS = 25;
type PreloadedArtworkMesh = { loadedMesh: any, imageDioaram: string, activeArtworkIndex?: number };
type ArtworkPlacementResult = {
  attached: boolean;
  staleCleanupCount?: number;
};
export type ArtworkLoadStrategy = "progressive" | "all";
export type ArtworkLoadTelemetrySnapshot = {
  phase: "initial_budget" | "cleanup";
  loadStrategy: ArtworkLoadStrategy;
  activeArtworkCount: number;
  visibleArtworkCount: number;
  pendingArtworkCount: number;
  disposedArtworkCount: number;
  staleArtworkCleanupCount: number;
};
type ArtworkLoaderRuntimeControls = {
  isCurrent?: () => boolean;
  createAssetHandle?: (kind: string, label?: string) => ArtetraRuntimeAssetHandle | null | undefined;
  useProgressiveLoading?: () => boolean;
  useOptimizedAssets?: () => boolean;
  getMediaDelivery?: () => RuntimeMediaDelivery | null;
  onTelemetry?: (snapshot: ArtworkLoadTelemetrySnapshot) => void;
};

type ArtworkRuntimeHandleEntry = {
  accountingHandle: DisposalAccountingHandle;
  runtimeHandle?: ArtetraRuntimeAssetHandle | null;
};

export type LoadArtworksOptions = {
  useProgressiveLoading?: boolean;
  useOptimizedAssets?: boolean;
  mediaDelivery?: RuntimeMediaDelivery | null;
};

/**
 * Manages loading and display of artworks in the 3D space
 * Handles artwork placement, materials, textures, and interactive elements
 */
class ArtworkLoader {
  /** Collection of proximity-based artwork interactions */
  artworkProximityActions = [] as any;
  /** Collection of artwork collision boundaries */
  artworkCollisionCollection = [] as any;
  /** Collection of clickable artwork elements */
  artworkClickCollection = [] as any;
  /** Space configuration data */
  spaceData = undefined as any;
  /** Asset loader instance */
  loader: Loader;
  /** Utility functions */
  utils: Utils;
  /** Sound service for handling audio */
  soundService: SoundService;
  preLoaderMesh: any;
  private slotLoadTokens = new Map<number, number>();
  private runtimeControls: ArtworkLoaderRuntimeControls = {};
  private disposalAccounting: DisposalAccounting = createDisposalAccounting();
  private activeArtworkHandlesBySlot = new Map<number, ArtworkRuntimeHandleEntry>();
  private artworkLoadGeneration = 0;
  private staleArtworkCleanupCount = 0;
  private currentLoadStrategy: ArtworkLoadStrategy = "all";
  private loadedMeshDisposer: ((loadedMesh: any) => any) | null = null;

  private isRuntimeAlive() {
    return Boolean(appData?.artworksScene && appData?.spaceScene && this.loader && this.utils);
  }

  private isRuntimeCurrent() {
    return this.runtimeControls.isCurrent ? this.runtimeControls.isCurrent() : true;
  }

  private shouldUseOptimizedAssets(explicit?: boolean) {
    return explicit ?? this.runtimeControls.useOptimizedAssets?.() ?? false;
  }

  private getMediaDelivery(explicit?: RuntimeMediaDelivery | null) {
    return explicit === undefined
      ? this.runtimeControls.getMediaDelivery?.() || null
      : explicit;
  }

  private captureLoadedMeshDisposer() {
    const disposer = this.utils?.disposeOfChildren;
    if (typeof disposer === "function") {
      this.loadedMeshDisposer = disposer.bind(this.utils);
    }
  }

  private isPseudoMoMSpace() {
    return appData.spaceData?.subtype === "pseudoMoM";
  }

  private shouldRestoreSlotScaleForBlankPlaceholder() {
    if (this.isPseudoMoMSpace()) return false;
    const gallerySpaceType = String(appData.galleryData?.spaceType || "").toLowerCase();
    return gallerySpaceType !== "mom" && gallerySpaceType !== "sellermom";
  }

  private restoreSlotScaleForBlankPlaceholder(slot: number, slotObject: THREE.Object3D) {
    if (!this.shouldRestoreSlotScaleForBlankPlaceholder()) return;
    const slotIndex = Number(slot);
    const sourceSlots = Array.isArray(appData.spaceData?.data?.slots) ? appData.spaceData.data.slots : [];
    const sourceSlot = sourceSlots.find((spaceSlot: any) => Number(spaceSlot?.index) === slotIndex) || sourceSlots[slotIndex];
    const scale = sourceSlot?.transforms?.scale;
    if (!scale) return;

    const finiteOrCurrent = (value: any, current: number) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : current;
    };

    slotObject.scale.set(
      finiteOrCurrent(scale.x, slotObject.scale.x),
      finiteOrCurrent(scale.y, slotObject.scale.y),
      finiteOrCurrent(scale.z, slotObject.scale.z)
    );
  }

  private startSlotLoad(slotId: number) {
    const nextToken = (this.slotLoadTokens.get(slotId) || 0) + 1;
    this.slotLoadTokens.set(slotId, nextToken);
    return nextToken;
  }

  private invalidateAllSlotLoads() {
    this.slotLoadTokens.forEach((token, slotId) => {
      this.slotLoadTokens.set(slotId, token + 1);
    });
  }

  private isCurrentSlotLoad(slotId: number, token: number) {
    return this.slotLoadTokens.get(slotId) === token;
  }

  private queueRender() {
    if (!this.isRuntimeAlive() || !this.isRuntimeCurrent()) return;
    appData?.renderService?.addToRenderQueue?.();
  }

  private boundedTelemetryCount(value: number) {
    return Math.min(10000, Math.max(0, Math.floor(Number(value) || 0)));
  }

  private recordStaleArtworkCleanup(
    loadGeneration: number,
    placementResult: ArtworkPlacementResult
  ) {
    if (this.artworkLoadGeneration !== loadGeneration) return;
    this.staleArtworkCleanupCount += this.boundedTelemetryCount(
      placementResult.staleCleanupCount || 0
    );
  }

  private emitTelemetry(
    phase: ArtworkLoadTelemetrySnapshot["phase"],
    pendingArtworkCount = 0
  ) {
    const accounting = this.disposalAccounting.snapshot().totals;
    const visibleArtworkCount = Array.from(this.activeArtworkHandlesBySlot.keys())
      .filter((slotId) => this.isSlotVisible(slotId))
      .length;

    this.runtimeControls.onTelemetry?.({
      phase,
      loadStrategy: this.currentLoadStrategy,
      activeArtworkCount: this.boundedTelemetryCount(accounting.active),
      visibleArtworkCount: this.boundedTelemetryCount(visibleArtworkCount),
      pendingArtworkCount: this.boundedTelemetryCount(pendingArtworkCount),
      disposedArtworkCount: this.boundedTelemetryCount(accounting.disposed),
      staleArtworkCleanupCount: this.boundedTelemetryCount(this.staleArtworkCleanupCount),
    });
  }

  private cleanupLoadedMesh(loadedMesh?: any) {
    if (!loadedMesh) return;
    cleanupArtworkRuntimeRegistries({ root: loadedMesh });
    this.captureLoadedMeshDisposer();
    this.loadedMeshDisposer?.(loadedMesh);
  }

  private releaseArtworkHandleForSlot(slotId: number, disposed = false) {
    const slotIndex = Number(slotId);
    if (!Number.isFinite(slotIndex)) return;
    const handleEntry = this.activeArtworkHandlesBySlot.get(slotIndex);
    if (!handleEntry) return;

    this.disposalAccounting.release(handleEntry.accountingHandle, { disposed });
    handleEntry.runtimeHandle?.release?.();
    this.activeArtworkHandlesBySlot.delete(slotIndex);
  }

  private releaseAllArtworkHandles(disposed = false) {
    Array.from(this.activeArtworkHandlesBySlot.keys()).forEach((slotId) => {
      this.releaseArtworkHandleForSlot(slotId, disposed);
    });
  }

  private registerArtworkHandleForSlot(slotId: number) {
    const slotIndex = Number(slotId);
    if (!Number.isFinite(slotIndex)) return;

    this.releaseArtworkHandleForSlot(slotIndex, true);
    this.activeArtworkHandlesBySlot.set(slotIndex, {
      accountingHandle: this.disposalAccounting.register("artwork"),
      runtimeHandle: this.runtimeControls.createAssetHandle?.("artwork", `slot-${slotIndex}`),
    });
  }

  private getSlotDistanceToCamera(slotId: number) {
    const slotObject = this.utils.getSlotObjectBySlotTarget?.(slotId, appData);
    if (!slotObject) return Number.POSITIVE_INFINITY;

    const playerWorldPosition =
      appData.physicsService?.playerCollider?.end?.clone?.() ||
      appData.camera?.position?.clone?.() ||
      new THREE.Vector3();
    const slotWorldPosition = new THREE.Vector3();

    if (typeof slotObject.getWorldPosition === "function") {
      slotObject.getWorldPosition(slotWorldPosition);
    } else if (slotObject.position) {
      slotWorldPosition.copy(slotObject.position);
    }

    return slotWorldPosition.distanceTo(playerWorldPosition);
  }

  private isSlotVisible(slotId: number) {
    const slotObject = this.utils.getSlotObjectBySlotTarget?.(slotId, appData);
    return Boolean(slotObject && slotObject.visible !== false);
  }

  private objectBelongsToSlot(object: any, slotIndex: number) {
    const userData = object?.userData || {};
    const directSlotTarget = Number(userData.slotTarget);
    const directIndex = Number(userData.index);
    const wrapperSlotTarget = Number(userData.artworkWrapper?.slotTarget);

    return directSlotTarget === slotIndex || directIndex === slotIndex || wrapperSlotTarget === slotIndex;
  }

  private clearDetachedPseudoMoMSlotObjects(slot: number, slotObject?: THREE.Object3D) {
    if (!this.isPseudoMoMSpace() || !appData.artworksScene) return false;

    const slotIndex = Number(slot);
    if (!Number.isFinite(slotIndex)) return false;
    let removedAnyObject = false;

    [...appData.artworksScene.children].forEach((child: any) => {
      if (child === slotObject) return;

      let belongsToSlot = this.objectBelongsToSlot(child, slotIndex);
      child.traverse?.((nestedChild: any) => {
        if (belongsToSlot) return;
        belongsToSlot = this.objectBelongsToSlot(nestedChild, slotIndex);
      });

      if (!belongsToSlot) return;

      this.utils.disposeOfChildren(child);
      child.removeFromParent?.();
      removedAnyObject = true;
    });

    if (removedAnyObject) {
      this.queueRender();
    }

    return removedAnyObject;
  }

  private hasRenderableArtworkAtSlot(slotIndex: number): boolean {
    return Boolean(appData.galleryData?.artworkWrappers?.some((wrapper: ArtworkWrapper) => {
      if (Number(wrapper?.slotTarget) !== slotIndex) return false;
      const activeArtworkIndex = Number(wrapper.activeArtwork);
      return Number.isInteger(activeArtworkIndex) && Boolean(wrapper.artworks?.[activeArtworkIndex]);
    }));
  }

  /**
   * Initializes the artwork loader with required services
   * @param loader Asset loader instance for loading artwork resources
   */
  constructor(loader, soundService) {
    this.utils = new Utils();
    this.captureLoadedMeshDisposer();
    this.loader = loader;
    this.soundService = soundService;
    // appData.artworksScene = new THREE.Scene()
  }

  public setRuntimeControls(runtimeControls: ArtworkLoaderRuntimeControls | null) {
    this.runtimeControls = {
      ...this.runtimeControls,
      ...(runtimeControls || {}),
    };
  }

  public getDisposalAccountingSnapshot(): DisposalAccountingSnapshot {
    return this.disposalAccounting.snapshot();
  }

  /**
   * Loads all artworks into the scene
   * Creates artwork slots and populates them with artwork or placeholders
   */
  public async loadArtworks(options: LoadArtworksOptions = {}) {
    const loadGeneration = ++this.artworkLoadGeneration;
    this.staleArtworkCleanupCount = 0;
    const useProgressiveLoading = options.useProgressiveLoading
      ?? this.runtimeControls.useProgressiveLoading?.()
      ?? false;
    const loadStrategy: ArtworkLoadStrategy = useProgressiveLoading ? "progressive" : "all";
    const artworkLoadOptions: LoadArtworksOptions = {
      ...options,
      useOptimizedAssets: this.shouldUseOptimizedAssets(options.useOptimizedAssets),
      mediaDelivery: this.getMediaDelivery(options.mediaDelivery),
    };
    this.currentLoadStrategy = loadStrategy;
    const queuedSlotTokens = new WeakMap<object, number>();
    const preloadQueuedArtwork = (artworkWrapper: ArtworkWrapper) => {
      const slotTarget = Number(artworkWrapper?.slotTarget);
      if (Number.isInteger(slotTarget) && slotTarget >= 0) {
        queuedSlotTokens.set(artworkWrapper, this.startSlotLoad(slotTarget));
      }
      return this.preloadArtworkMesh(artworkWrapper, artworkLoadOptions);
    };
    try {
      cleanupArtworkRuntimeRegistries();

      return await new Promise(async (resolve, reject) => {
        try {
          if (
            !this.isRuntimeAlive() ||
            !this.isRuntimeCurrent() ||
            this.artworkLoadGeneration !== loadGeneration
          ) {
            resolve({ loadStrategy });
            return;
          }

          // clear all data first
          if (appData.artworksScene && appData.artworksScene.children) {
            this.releaseAllArtworkHandles(true);
            this.utils.disposeOfChildren(appData.artworksScene, true)
          }

          // load slots
          await this.loadSlots(() => (
            this.isRuntimeAlive() &&
            this.isRuntimeCurrent() &&
            this.artworkLoadGeneration === loadGeneration
          ))
          if (
            !this.isRuntimeAlive() ||
            !this.isRuntimeCurrent() ||
            this.artworkLoadGeneration !== loadGeneration
          ) {
            resolve({ loadStrategy });
            return;
          }

          // insert proloaders before loading
          const artworkWrapperKeys = Object.keys(appData.galleryData.artworkWrappers)
          for (let index = 0; index < artworkWrapperKeys.length; index++) {
            const artwork = artworkWrapperKeys[index]
            const artworkWrapper = appData.galleryData.artworkWrappers[artwork]
            const hasValidSlotTarget = Number.isInteger(artworkWrapper?.slotTarget) && artworkWrapper.slotTarget >= 0;
            const hasActiveArtwork = artworkWrapper?.artworks?.length && artworkWrapper.artworks[artworkWrapper.activeArtwork];
            if (hasValidSlotTarget && hasActiveArtwork) {
              this.insertPreloaderBeforeLoading(artworkWrapper, artworkWrapper.slotTarget)
              // this.loadArtworkInSlot(artworkWrapper, artworkWrapper.slotTarget)
            }
          }

          let loadSummary: ProgressiveArtworkQueueSummary | Record<string, never> = {};
          const loadArtworkForGeneration = async (
            artworkWrapper: ArtworkWrapper,
            slotId: number,
            preloadData?: PreloadedArtworkMesh | null
          ) => {
            const placementResult = await this.loadArtworkInSlot(
              artworkWrapper,
              slotId,
              preloadData || undefined,
              queuedSlotTokens.get(artworkWrapper),
              loadGeneration,
              artworkLoadOptions
            );
            this.recordStaleArtworkCleanup(loadGeneration, placementResult);
            return placementResult;
          };
          if (!useProgressiveLoading) {
            await ArtworkHandler.loadArtworksInDistanceOrder(
              this.utils,
              appData,
              preloadQueuedArtwork,
              loadArtworkForGeneration,
              () => (
                this.isRuntimeAlive() &&
                this.isRuntimeCurrent() &&
                this.artworkLoadGeneration === loadGeneration
              ),
              (preloadData) => this.cleanupLoadedMesh(preloadData?.loadedMesh)
            );
          } else {
            const rankedArtworkWrappers = rankArtworkWrappersForProgressiveLoad({
              wrappers: appData.galleryData.artworkWrappers,
              getSlotVisibility: (slotTarget) => this.isSlotVisible(slotTarget),
              getSlotDistance: (slotTarget) => this.getSlotDistanceToCamera(slotTarget),
            });

            loadSummary = await runProgressiveArtworkQueue({
              rankedWrappers: rankedArtworkWrappers,
              initialBudget: PROGRESSIVE_INITIAL_BUDGET,
              backgroundConcurrency: PROGRESSIVE_BACKGROUND_CONCURRENCY,
              preloadArtwork: preloadQueuedArtwork,
              loadArtworkInSlot: loadArtworkForGeneration,
              isCurrent: () => (
                this.isRuntimeAlive() &&
                this.isRuntimeCurrent() &&
                this.artworkLoadGeneration === loadGeneration
              ),
              cleanupPreload: (preloadData) => {
                this.cleanupLoadedMesh(preloadData?.loadedMesh);
                if (this.artworkLoadGeneration === loadGeneration) {
                  this.staleArtworkCleanupCount += 1;
                }
              },
              delay: () => ArtworkHandler.delay(PROGRESSIVE_BACKGROUND_DELAY_MS),
              onInitialBudgetComplete: (summary) => {
                const completedCount = (
                  summary.loaded +
                  summary.skipped +
                  summary.stale +
                  summary.failed
                );
                this.emitTelemetry(
                  "initial_budget",
                  rankedArtworkWrappers.length - completedCount
                );
              },
            });
          }

          if (
            !this.isRuntimeAlive() ||
            this.artworkLoadGeneration !== loadGeneration
          ) {
            resolve({ loadStrategy });
            return;
          }

          resolve({ ...loadSummary, loadStrategy })
        } catch (error) {
          console.error("ArtworkLoader: Error in loadArtworks internal promise", error);
          reject(error);
        }
      });
    } catch (error) {
      console.error("ArtworkLoader: Error in loadArtworks", error);
      throw error;
    }
  }

  private async loadSlots(isCurrent: () => boolean = () => this.isRuntimeAlive() && this.isRuntimeCurrent()) {
    const artworkPromises = []

    const sortedSlots = this.utils.sortByIndex(appData.spaceData.data.slots)
    Object.keys(sortedSlots).map((key) => {
      const slot: Slot = sortedSlots[key] as Slot;
      slot.artworkWrapper = null
      let slotObject = new THREE.Object3D()
      slotObject.position.set(slot.transforms.position.x, slot.transforms.position.y, slot.transforms.position.z)
      slotObject.rotation.set(slot.transforms.rotation.x, slot.transforms.rotation.y, slot.transforms.rotation.z)
      slotObject.scale.set(slot.transforms.scale.x, slot.transforms.scale.y, slot.transforms.scale.z)
      slotObject.userData = slot
      slotObject.name = "Slot-" + slot.index

      appData.artworksScene.add(slotObject)

      // To-DO: make this more efficient
      if (
        appData.galleryData.editable &&
        EDITABLE_SPACE_TYPES.includes(appData.spaceData.type) &&
        !this.hasRenderableArtworkAtSlot(slot.index)
      ) {
        artworkPromises.push(ArtworkHandler.addImageHolderForBlankArtworks(
          this.loader,
          slotObject,
          isCurrent,
          (loadedMesh) => this.cleanupLoadedMesh(loadedMesh)
        ))
      }
    });
    // ensure it loads in order
    return await Promise.all(artworkPromises);
  }

  private createLoadingPlane(type: "image" | "3dmodel" | "diorama" | "video" | "sound"): THREE.Sprite | THREE.Mesh {

    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Transparent background: do not fill background
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.font = "bold 220px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Draw white stroke
      ctx.lineWidth = 12;
      ctx.strokeStyle = "#000";
      ctx.strokeText("Loading...", canvas.width / 2, canvas.height / 2);

      // Draw black fill
      ctx.fillStyle = "#fff";
      ctx.fillText("Loading...", canvas.width / 2, canvas.height / 2);
    }
    // Create texture from the canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    if (type === "image" || type === "video" || type === "sound") {
      const geometry = new THREE.PlaneGeometry(1, 0.5);
      const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
      const loadingPlane = new THREE.Mesh(geometry, material);
      return loadingPlane;
    }
    else {
      const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
      const loadingSprite = new THREE.Sprite(material);
      loadingSprite.scale.set(1, 0.5, 1);
      return loadingSprite;
    }




  }

  private insertPreloaderBeforeLoading(artworkWrapper: ArtworkWrapper, slotId: number) {
    if (!artworkWrapper?.artworks?.length || !artworkWrapper.artworks[artworkWrapper.activeArtwork]) {
      return;
    }
    const slotObject = this.utils.getSlotObjectBySlotTarget(slotId, appData)
    if (!slotObject) {
      console.warn(`ArtworkLoader: Missing slot object for slotTarget ${slotId}. Skipping preloader insertion.`);
      return;
    }
    const activeArtwork = artworkWrapper.artworks[artworkWrapper.activeArtwork]
    const loadingPlane = this.createLoadingPlane(activeArtwork.type);
    if (artworkWrapper?.transforms?.position) loadingPlane.position.set(artworkWrapper.transforms.position.x, artworkWrapper.transforms.position.y, artworkWrapper.transforms.position.z)
    loadingPlane.position.z += 0.1
    slotObject.add(loadingPlane);
  }

  public updateSound(artwork: Artwork) {
    const height = artwork.artworkData.height / 100
    //@ts-ignore
    const loadedMesh = artwork.artworkData.sound.userData.loadedMesh
    if (height && loadedMesh) {
      const heightFactor = height / 0.7491
      loadedMesh.scale.x = heightFactor
      loadedMesh.scale.y = heightFactor
      loadedMesh.scale.z = heightFactor
    }
  }

  public updateArtworkPedestal(slot: number, artworkWrapper: ArtworkWrapper | null, listing: boolean) {
    const activeArtwork = artworkWrapper.artworks[artworkWrapper.activeArtwork];
    if (activeArtwork.artworkData.pedestal && activeArtwork.artworkData.pedestal.mesh) {
      ArtworkPedestal.updatePedestalType(activeArtwork);
    }
  }

  public async listArtworkInSlot(slot: number, artworkWrapper: ArtworkWrapper | null, listing: boolean) {
    try {
      return await new Promise(async (resolve, reject) => {
        try {
          if (!this.isRuntimeAlive()) {
            resolve({});
            return;
          }

          const slotObject = this.utils.getSlotObjectBySlotTarget(slot, appData)
          if (!slotObject) {
            this.startSlotLoad(slot);
            this.releaseArtworkHandleForSlot(slot, true);
            this.clearDetachedPseudoMoMSlotObjects(slot);
            resolve({});
            return;
          }

          this.clearDetachedPseudoMoMSlotObjects(slot, slotObject);

          if (listing) {

            this.removePedestalColider(slotObject.name)
            cleanupArtworkRuntimeRegistries({ slotTarget: slot });

            this.utils.disposeOfChildren(slotObject, true)
            await this.loadArtworkInSlot(artworkWrapper, slot);
            this.queueRender()
            resolve({})
          } else {
            const loadToken = this.startSlotLoad(slot);
            const isPlaceholderCurrent = () => (
              this.isRuntimeAlive() &&
              this.isRuntimeCurrent() &&
              this.isCurrentSlotLoad(slot, loadToken)
            );
            this.releaseArtworkHandleForSlot(slot, true);
            this.removePedestalColider(slotObject.name)
            cleanupArtworkRuntimeRegistries({ slotTarget: slot });
            // remove existing data and add the default artwork
            this.utils.disposeOfChildren(slotObject, true);
            this.restoreSlotScaleForBlankPlaceholder(slot, slotObject);
            this.queueRender();
            try {
              await ArtworkHandler.addImageHolderForBlankArtworks(
                this.loader,
                slotObject,
                isPlaceholderCurrent,
                (loadedMesh) => this.cleanupLoadedMesh(loadedMesh)
              );
            } catch (error) {
              console.error("ArtworkLoader: Error adding blank artwork placeholder", error);
            }
            if (!isPlaceholderCurrent()) {
              resolve({});
              return;
            }
            this.queueRender();
            appData.artworksScene.add(slotObject);


            // reset slot object user data including children
            slotObject.traverse((child) => {
              child.userData = { ...slotObject.userData, artworkWrapper: null };
            });
            slotObject.userData = { ...slotObject.userData, artworkWrapper: null };
            resolve({})
          }
        } catch (error) {
          console.error("ArtworkLoader: Error in listArtworkInSlot", error);
          reject(error);
        }
      });
    } catch (error) {
      console.error("ArtworkLoader: Error in listArtworkInSlot outer", error);
      throw error;
    }
  }

  private removePedestalColider(slotName) {
    const octreeHelper = appData.spaceScene.getObjectByName("octree-mesh")

    if (octreeHelper) {
      for (let i = octreeHelper.children.length - 1; i >= 0; i--) {
        const child = octreeHelper.children[i]
        if (child.userData?.slotName === slotName) {
          child.removeFromParent()
        }
      }
    }
  }

  /**
   * Loads a single artwork into its designated slot
   * Handles both diorama and image type artworks
   * @param artworkData Configuration data for the artwork
   * @returns Promise resolving when artwork is loaded
   */
  public async loadArtworkInSlot(
    artworkWrapper: ArtworkWrapper,
    slotId: number,
    preloadData?: PreloadedArtworkMesh,
    expectedLoadToken?: number,
    expectedLoadGeneration?: number,
    options: LoadArtworksOptions = {}
  ): Promise<ArtworkPlacementResult> {
    try {
      return await new Promise(async (resolve, reject) => {
        try {
          if (!this.isRuntimeAlive()) {
            this.cleanupLoadedMesh(preloadData?.loadedMesh);
            resolve({
              attached: false,
              staleCleanupCount: preloadData?.loadedMesh ? 1 : 0,
            });
            return;
          }

          const loadToken = expectedLoadToken ?? this.startSlotLoad(slotId);
          const isLoadCurrent = () => (
            this.isRuntimeCurrent() &&
            this.isCurrentSlotLoad(slotId, loadToken) &&
            (
              expectedLoadGeneration === undefined ||
              this.artworkLoadGeneration === expectedLoadGeneration
            )
          );

          if (!isLoadCurrent()) {
            this.cleanupLoadedMesh(preloadData?.loadedMesh);
            resolve({
              attached: false,
              staleCleanupCount: preloadData?.loadedMesh ? 1 : 0,
            });
            return;
          }

          if (artworkWrapper.artworks && artworkWrapper.artworks.length) {
            this.releaseArtworkHandleForSlot(slotId, true);
            const activeArtworkIndex = Number(artworkWrapper.activeArtwork);
            if (!Number.isInteger(activeArtworkIndex) || !artworkWrapper.artworks[activeArtworkIndex]) {
              this.cleanupLoadedMesh(preloadData?.loadedMesh);
              resolve({ attached: false });
              return;
            }
            const artworkWrapperSnapshot = {
              ...artworkWrapper,
              activeArtwork: activeArtworkIndex,
              artworks: [...artworkWrapper.artworks],
            } as ArtworkWrapper;
            const canonicalArtwork = artworkWrapperSnapshot.artworks[artworkWrapperSnapshot.activeArtwork];
            const activeArtwork = canonicalArtwork
              ? resolveArtworkForRuntimeMedia(
                canonicalArtwork,
                this.getMediaDelivery(options.mediaDelivery),
              )
              : canonicalArtwork;
            artworkWrapperSnapshot.artworks[artworkWrapperSnapshot.activeArtwork] = activeArtwork;

            // add frame if it doesn't exist
            ArtworkFrame.ensureFrameData(artworkWrapperSnapshot);

            let artworkFrameUrl = ArtworkFrame.getFrameUrl(activeArtwork.artworkData.frame.type);

            const canUsePreloadedMesh = preloadData?.loadedMesh && (
              preloadData.activeArtworkIndex === undefined ||
              preloadData.activeArtworkIndex === activeArtworkIndex
            );
            if (preloadData?.loadedMesh && !canUsePreloadedMesh) {
              this.cleanupLoadedMesh(preloadData.loadedMesh);
            }
            let loadedMesh = canUsePreloadedMesh ? preloadData.loadedMesh : null;
            let imageDioaram = canUsePreloadedMesh ? preloadData.imageDioaram : "diorama";

            if (!loadedMesh) {
              const artworkModelSource = resolveArtworkModelSource(activeArtwork, artworkFrameUrl, {
                useOptimizedAssets: this.shouldUseOptimizedAssets(options.useOptimizedAssets),
                mediaDelivery: this.getMediaDelivery(options.mediaDelivery),
              });

              loadedMesh = await this.loader.load3DModel(artworkModelSource.source, true);
              if (!this.isRuntimeAlive()) {
                this.cleanupLoadedMesh(loadedMesh);
                resolve({ attached: false, staleCleanupCount: loadedMesh ? 1 : 0 });
                return;
              }
              if (isLoadCurrent && !isLoadCurrent()) {
                this.cleanupLoadedMesh(loadedMesh);
                resolve({ attached: false, staleCleanupCount: loadedMesh ? 1 : 0 });
                return;
              }
              if (!loadedMesh) {
                console.warn("ArtworkLoader: Failed to resolve artwork mesh", {
                  artworkId: activeArtwork._id,
                  artworkType: activeArtwork.type,
                  source: artworkModelSource.source
                });
                resolve({ attached: false });
                return;
              }

              imageDioaram = artworkModelSource.imageDioaram;
            }
            if (isLoadCurrent && !isLoadCurrent()) {
              this.cleanupLoadedMesh(loadedMesh);
              resolve({ attached: false, staleCleanupCount: loadedMesh ? 1 : 0 });
              return;
            }
            const artworkType = activeArtwork.type;
            const handlerArtworkWrapper = artworkWrapperSnapshot;
            const currentLoadGuard = isLoadCurrent;
            let placementResult: ArtworkPlacementResult = { attached: false };
            switch (artworkType) {
              case "diorama":
                placementResult = await Artwork3dModel.handleSpaceArtworkSlot(this.loader, this.utils, appData, handlerArtworkWrapper, loadedMesh, imageDioaram, slotId, currentLoadGuard);
                if (placementResult.attached && currentLoadGuard()) {
                  this.registerArtworkHandleForSlot(slotId);
                  this.queueRender()
                }
                resolve(placementResult);
                break;
              case "video":
              case "image":
                placementResult = await ArtworkHandler.handleImageArtworkSlot(this.loader, this.utils, appData, handlerArtworkWrapper, loadedMesh, slotId, currentLoadGuard);
                if (placementResult.attached && currentLoadGuard()) {
                  this.registerArtworkHandleForSlot(slotId);
                  this.queueRender()
                }
                resolve(placementResult);
                break;
              case "3dmodel":
                placementResult = await Artwork3dModel.handle3dmodelArtworkSlot(this.utils, appData, handlerArtworkWrapper, loadedMesh, slotId, this.loader, currentLoadGuard);
                if (placementResult.attached && currentLoadGuard()) {
                  this.registerArtworkHandleForSlot(slotId);
                  this.utils.handleProcedrualWallCollision(appData)
                  this.queueRender()
                }
                resolve(placementResult);
                break;
              case "sound":
                placementResult = await ArtworkHandler.handleSoundArtworkSlot(this.utils, appData, this.soundService, handlerArtworkWrapper, loadedMesh, slotId, currentLoadGuard);
                if (placementResult.attached && currentLoadGuard()) {
                  this.registerArtworkHandleForSlot(slotId);
                  this.queueRender()
                }
                resolve(placementResult);
                break;
              default:
                this.cleanupLoadedMesh(loadedMesh);
                resolve({ attached: false });
            }
          } else {
            this.cleanupLoadedMesh(preloadData?.loadedMesh);
            resolve({ attached: false })
          }
        } catch (error) {
          console.error("ArtworkLoader: Error in loadArtworkInSlot", error);
          reject(error);
        }
      });
    } catch (error) {
      console.error("ArtworkLoader: Error in loadArtworkInSlot outer", error);
      throw error;
    }
  }

  /**
   * Removes all artworks from the scene
   */
  public removeAllArtwoks() {
    this.artworkLoadGeneration += 1;
    this.invalidateAllSlotLoads();
    cleanupArtworkRuntimeRegistries();
    this.releaseAllArtworkHandles(true);
    this.utils.disposeOfChildren(appData.artworksScene);
    this.emitTelemetry("cleanup");
  }

  public removeArtwork(slot: number) {
    this.startSlotLoad(slot);
    this.releaseArtworkHandleForSlot(slot, true);
    const slotObject = this.utils.getSlotObjectBySlotTarget(slot, appData);
    this.clearDetachedPseudoMoMSlotObjects(slot, slotObject);
    cleanupArtworkRuntimeRegistries({ slotTarget: slot });
    return this.utils.disposeOfChildren(slotObject, true)
  }

  /**
   * Cleans up and disposes of all artwork resources
   * Removes materials, geometries, textures, and clears collections
   */
  public dispose() {
    this.captureLoadedMeshDisposer();
    // Remove all artworks.
    this.removeAllArtwoks();

    // Nullify data to aid garbage collection
    appData.artworksScene = null;
    this.loader = null;
    this.utils = null;
    this.artworkClickCollection = [];
    this.artworkProximityActions = [];
    this.artworkCollisionCollection = [];
    this.slotLoadTokens.clear();
  }

  /**
   * Preloads the mesh required for an artwork without attaching it to the scene.
   * Returns the loaded mesh along with its inferred type to be reused later.
   */
  private async preloadArtworkMesh(
    artworkWrapper: ArtworkWrapper,
    options: LoadArtworksOptions = {}
  ): Promise<PreloadedArtworkMesh | null> {
    if (!artworkWrapper?.artworks?.length || !artworkWrapper.artworks[artworkWrapper.activeArtwork]) {
      return null;
    }

    // ensure frame defaults exist before accessing frame data
    ArtworkFrame.ensureFrameData(artworkWrapper);
    const activeArtworkIndex = Number(artworkWrapper.activeArtwork);
    const canonicalArtwork = artworkWrapper.artworks[activeArtworkIndex];
    const activeArtwork = canonicalArtwork
      ? resolveArtworkForRuntimeMedia(
        canonicalArtwork,
        this.getMediaDelivery(options.mediaDelivery),
      )
      : canonicalArtwork;
    if (!activeArtwork?.artworkData) {
      return null;
    }

    let artworkFrameUrl = ArtworkFrame.getFrameUrl(activeArtwork.artworkData.frame.type);
    const artworkModelSource = resolveArtworkModelSource(activeArtwork, artworkFrameUrl, {
      useOptimizedAssets: this.shouldUseOptimizedAssets(options.useOptimizedAssets),
      mediaDelivery: this.getMediaDelivery(options.mediaDelivery),
    });

    let loadedMesh = null;
    let imageDioaram = artworkModelSource.imageDioaram;
    loadedMesh = await this.loader.load3DModel(artworkModelSource.source, true);
    if (!this.isRuntimeAlive()) {
      this.cleanupLoadedMesh(loadedMesh);
      return null;
    }
    if (!loadedMesh) {
      console.warn("ArtworkLoader: Failed to preload artwork mesh", {
        artworkId: activeArtwork._id,
        artworkType: activeArtwork.type,
        source: artworkModelSource.source
      });
      return null;
    }

    return { loadedMesh, imageDioaram, activeArtworkIndex }
  }

}

export default ArtworkLoader;
